import { getChronicleDeviceProps } from '@/app-shell/chronicle/analytics/chronicle-device-props';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  DeviceEventLevelMeterSupported,
  DeviceEventPipeline,
  DeviceEventProps,
  DeviceEventRecordingStoppedOutcome,
  DeviceEventTrigger,
} from '@/models/analytics/features/chronicle';

/**
 * Gates every `device_event` destination (Snowplow, Amplitude, PostHog).
 * The schema is live in the production registry; the flip waits on the
 * Amplitude routing ruling for this event.
 */
export const DEVICE_EVENT_ENABLED = false;

/** Once per clinical session id — first pipeline selection only. */
const pipelineSelectedSessions = new Set<string>();

/** Test-only: clear the once-per-recording guard. */
export function resetRemoteDeviceEventGuardsForTests(): void {
  pipelineSelectedSessions.clear();
}

export function isDeviceEventEnabled(): boolean {
  return DEVICE_EVENT_ENABLED;
}

function deviceIdentityProps(): Pick<
  DeviceEventProps,
  'device_serial' | 'device_class' | 'firmware_version'
> {
  const device = getChronicleDeviceProps();
  const serial = device.hardware_serial_number;
  const firmware = device.hardware_fw_version;
  return {
    device_serial: typeof serial === 'string' && serial !== 'NA' ? serial : undefined,
    device_class: 'heidi_remote',
    firmware_version:
      typeof firmware === 'string' && firmware !== 'NA' ? firmware : undefined,
  };
}

function emitDeviceEvent(props: DeviceEventProps): void {
  if (!isDeviceEventEnabled()) {
    return;
  }
  track({
    event_name: TrackingEventName.DeviceEvent,
    props,
    options: { posthog: true },
  });
}

export function mapRouteToPipeline(
  route: 'chunk_pull' | 'stream' | 'unresolved' | 'refused'
): DeviceEventPipeline {
  switch (route) {
    case 'chunk_pull':
      return 'chunk_live';
    case 'stream':
      return 'legacy_mp3_stream';
    default:
      return 'unresolved';
  }
}

export function mapLevelMeterSupported(
  probed: boolean | null | undefined
): DeviceEventLevelMeterSupported {
  if (probed === true) return 'true';
  if (probed === false) return 'false';
  return 'not_probed';
}

/**
 * Once per Remote recording at first source/pipeline selection (4.x and 5.0).
 * Amplitude catalogue only.
 */
export function trackRemotePipelineSelected({
  sessionId,
  sessionIdLocal,
  pipeline,
  trigger,
  levelMeterSupported,
  firmwareVersion,
}: {
  sessionId?: string | null;
  sessionIdLocal: string;
  pipeline: DeviceEventPipeline;
  trigger: DeviceEventTrigger;
  levelMeterSupported?: DeviceEventLevelMeterSupported;
  firmwareVersion?: string | null;
}): void {
  if (pipelineSelectedSessions.has(sessionIdLocal)) {
    return;
  }
  pipelineSelectedSessions.add(sessionIdLocal);

  const identity = deviceIdentityProps();
  emitDeviceEvent({
    ...identity,
    device_action: 'pipeline_selected',
    pipeline,
    trigger,
    level_meter_supported: levelMeterSupported,
    firmware_version: firmwareVersion ?? identity.firmware_version,
    session_id: sessionId ?? undefined,
    session_id_local: sessionIdLocal,
  });
}

/**
 * Fail-closed / concurrent-start refusal as catalogue `device_event`
 * (`recording_started` + `outcome: blocked`).
 *
 * Gated by `DEVICE_EVENT_ENABLED`. While the gate is false, callers
 * must keep emitting legacy `hardware_recording_start_blocked` — this path
 * must not replace or suppress that catalogue event.
 */
export function trackRemoteRecordingStartBlocked({
  sessionId,
  sessionIdLocal,
  trigger,
  reason,
}: {
  sessionId?: string | null;
  sessionIdLocal: string;
  trigger: DeviceEventTrigger;
  reason?: string;
}): void {
  const identity = deviceIdentityProps();
  emitDeviceEvent({
    ...identity,
    device_action: 'recording_started',
    outcome: 'blocked',
    error_type: reason,
    trigger,
    session_id: sessionId ?? undefined,
    session_id_local: sessionIdLocal,
  });
}

/**
 * Recording stop complete — 4.x and 5.0; 5.0-only chunk metrics may be omitted.
 */
export function trackRemoteRecordingStopped({
  sessionId,
  sessionIdLocal,
  pipeline,
  recordingDurationSeconds,
  waitAfterStopSeconds,
  chunksTotal,
  chunksSyncedDuringRecording,
  chunksTail,
  chunksDeferredErr,
  reconnectsDuringRecording,
  backlogTransfersDuringRecording,
  outcome,
}: {
  sessionId?: string | null;
  sessionIdLocal: string;
  pipeline: DeviceEventPipeline;
  recordingDurationSeconds: number;
  waitAfterStopSeconds?: number | null;
  chunksTotal?: number | null;
  chunksSyncedDuringRecording?: number | null;
  chunksTail?: number | null;
  chunksDeferredErr?: number | null;
  reconnectsDuringRecording?: number | null;
  backlogTransfersDuringRecording?: number | null;
  outcome?: DeviceEventRecordingStoppedOutcome | null;
}): void {
  const identity = deviceIdentityProps();
  const isChunkLive = pipeline === 'chunk_live';

  emitDeviceEvent({
    ...identity,
    device_action: 'recording_stopped',
    pipeline,
    recording_duration_seconds: recordingDurationSeconds,
    wait_after_stop_seconds: waitAfterStopSeconds ?? undefined,
    session_id: sessionId ?? undefined,
    session_id_local: sessionIdLocal,
    ...(isChunkLive
      ? {
          chunks_total: chunksTotal ?? undefined,
          chunks_synced_during_recording: chunksSyncedDuringRecording ?? undefined,
          chunks_tail: chunksTail ?? undefined,
          chunks_deferred_err: chunksDeferredErr ?? undefined,
          reconnects_during_recording: reconnectsDuringRecording ?? undefined,
          backlog_transfers_during_recording:
            backlogTransfersDuringRecording ?? undefined,
          outcome: outcome ?? undefined,
        }
      : {}),
  });
}

export function mapStopOutcomeForDeviceEvent({
  recoveryPending,
  totalChunks,
  isChunkLive,
}: {
  recoveryPending: boolean;
  totalChunks: number;
  isChunkLive: boolean;
}): DeviceEventRecordingStoppedOutcome | undefined {
  if (!isChunkLive) return undefined;
  if (recoveryPending) return 'dirty_stop_upload_pending';
  if (totalChunks === 0) return 'all_zero_byte';
  return 'finalized';
}
